import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TelegramCard = () => {
  const [t, setT] = useState(null);
  const load = useCallback(() => axios.get(`${API}/telegram`).then((r) => setT(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  if (!t) return null;
  const test = async () => {
    try {
      await axios.post(`${API}/telegram/test`);
      toast.success("Test alert sent — check Telegram");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    }
  };
  const setNotify = async (values) => {
    await axios.post(`${API}/telegram/notify-settings`, values);
    toast.success("Notification settings saved");
    load();
  };
  const setEvery = async (every) => {
    await axios.post(`${API}/telegram/every`, { every });
    toast.success(every ? `Scheduled snapshots every ${every} min` : "Scheduled snapshots off (material-change updates continue)");
    load();
  };
  const toggle = async (enabled) => {
    await axios.post(`${API}/telegram/toggle`, { enabled });
    load();
  };

  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="telegram-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><Send className="w-5 h-5 text-[#22D3EE]" /> Phone alerts (Telegram)</h2>
      {!t.connected ? (
        <>
          <p className="text-sm text-slate-400 mt-2">Get informational updates only on material changes (status changes, big model-estimate moves, data errors) plus official window results — with cooldowns so it never spams. Not trading advice.</p>
          <ol className="text-sm text-slate-300 mt-3 space-y-1 list-decimal list-inside">
            <li>Tap the button below to open your bot in Telegram.</li>
            <li>Press <b>Start</b>.</li>
            <li>This box turns green within a few seconds.</li>
          </ol>
          <a href={t.bot_url} target="_blank" rel="noreferrer" data-testid="telegram-connect-btn"
            className="inline-flex mt-4 px-5 py-2.5 rounded-full bg-[#229ED9] hover:bg-[#1B8BC0] text-white text-sm font-semibold transition-colors">
            Open my Telegram bot
          </a>
          <p className="text-[11px] text-slate-500 mt-2" data-testid="telegram-status">Status: waiting for you to press Start…</p>
        </>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-[#34D399] font-semibold" data-testid="telegram-status">Connected{t.name ? ` as ${t.name}` : ""}</div>
            <div className="text-[11px] text-slate-500">In Telegram: /status · /cooldown 1-60 · /sensitivity 5-50 · /max 1-10 · /every 5-60|off · /finals on|off · /recap · /stop</div>
            {[["cooldown_sec", "Cooldown", [[120, "2 min"], [300, "5 min"], [600, "10 min"]]],
              ["min_change_pts", "Material change", [[10, "10 pts"], [15, "15 pts"], [25, "25 pts"]]],
              ["max_per_window", "Max per window", [[1, "1"], [3, "3"], [5, "5"]]]].map(([key, label, opts]) => (
              <div key={key} className="flex flex-wrap items-center gap-1 mt-2" data-testid={`telegram-${key}`}>
                <span className="text-[11px] text-slate-400 mr-1">{label}:</span>
                {opts.map(([v, l]) => (
                  <button key={v} onClick={() => setNotify({ [key]: v })} data-testid={`telegram-${key}-${v}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${t[key] === v ? "bg-[#22D3EE] border-[#22D3EE] text-[#03141A]" : "border-[#1A2338] text-slate-300 hover:border-[#22D3EE]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-1 mt-2" data-testid="telegram-every">
              <span className="text-[11px] text-slate-400 mr-1">Scheduled snapshots:</span>
              {[5, 15, 0].map((m) => (
                <button key={m} onClick={() => setEvery(m)} data-testid={`telegram-every-${m || "off"}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${t.every === m ? "bg-[#22D3EE] border-[#22D3EE] text-[#03141A]" : "border-[#1A2338] text-slate-300 hover:border-[#22D3EE]"}`}>
                  {m ? `${m} min` : "Off"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={t.enabled} onCheckedChange={toggle} data-testid="telegram-toggle" />
              {t.enabled ? "Alerts on" : "Paused"}
            </label>
            <button onClick={test} className="px-4 py-2 rounded-full border border-[#1A2338] text-sm hover:border-[#22D3EE] transition-colors" data-testid="telegram-test-btn">Send test</button>
          </div>
        </div>
      )}
    </section>
  );
};
